'use client';

import { useState, useMemo } from 'react';
import { ShoppingBag, Plus, SlidersHorizontal, Check, Star, Heart } from 'lucide-react';
import { PRODUCTS, CATEGORIES, Product } from '@/lib/products';
import { useCart } from '@/lib/cart-store';
import { useWishlist } from '@/lib/wishlist-store';
import { getAverageRating, getReviewCount } from '@/lib/reviews';
import ProductModal from './product-modal';

type SortBy = 'featured' | 'price-asc' | 'price-desc' | 'name' | 'rating';

export default function Shop() {
  const [activeCategory, setActiveCategory] = useState<string>('Toutes');
  const [sortBy, setSortBy] = useState<SortBy>('featured');
  const [added, setAdded] = useState<string | null>(null);
  const [selected, setSelected] = useState<Product | null>(null);
  const { addItem } = useCart();
  const { toggle: toggleWishlist, has: inWishlist } = useWishlist();

  const filtered = useMemo(() => {
    let result =
      activeCategory === 'Toutes'
        ? PRODUCTS
        : PRODUCTS.filter((p) => p.category === activeCategory);

    switch (sortBy) {
      case 'price-asc':
        result = [...result].sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result = [...result].sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result = [...result].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'rating':
        result = [...result].sort((a, b) => getAverageRating(b.id) - getAverageRating(a.id));
        break;
      case 'featured':
      default:
        result = [...result].sort((a, b) => {
          if (a.tag && !b.tag) return -1;
          if (!a.tag && b.tag) return 1;
          return 0;
        });
    }
    return result;
  }, [activeCategory, sortBy]);

  const handleAdd = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      category: product.category,
    });
    setAdded(product.id);
    setTimeout(() => setAdded(null), 1500);
  };

  const handleWishlist = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    toggleWishlist({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      category: product.category,
    });
  };

  const handleCardClick = (product: Product) => {
    setSelected(product);
  };

  const categoryCount = (cat: string) =>
    cat === 'Toutes'
      ? PRODUCTS.length
      : PRODUCTS.filter((p) => p.category === cat).length;

  return (
    <>
      <section
        id="boutique-shop"
        className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
      >
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="block h-px w-10 bg-[#D4AF37]" />
              <span className="eyebrow">La Boutique</span>
              <span className="block h-px w-10 bg-[#D4AF37]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              <span className="text-[#F4E8C1]">Composez votre</span>{' '}
              <span className="text-gold-gradient">arithmétique du cuir.</span>
            </h2>
            <p className="max-w-2xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
              {PRODUCTS.length} pièces d'exception, {CATEGORIES.length} familles.
              Cliquez sur un produit pour détails + avis. Cœur pour ajouter aux
              favoris. Panier persistant.
            </p>
          </div>

          {/* Filters bar */}
          <div className="sticky top-16 z-30 bg-[#0a0a0f]/95 backdrop-blur-md border-y border-[#D4AF37]/15 py-4 mb-10">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex flex-wrap gap-2 items-center max-w-full">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/70 flex items-center gap-2 mr-2">
                  <SlidersHorizontal size={12} />
                  Catégories
                </span>
                {['Toutes', ...CATEGORIES].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border transition-all duration-300 ${
                      activeCategory === cat
                        ? 'bg-gold-gradient border-[#D4AF37] text-[#0a0a0f] font-semibold'
                        : 'border-[#D4AF37]/25 text-[#F4E8C1]/70 hover:border-[#D4AF37]/55 hover:text-[#F4E8C1]'
                    }`}
                  >
                    {cat}
                    <span className={`ml-1.5 ${activeCategory === cat ? 'text-[#0a0a0f]/60' : 'text-[#D4AF37]/60'}`}>
                      {categoryCount(cat)}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/70">
                  Trier
                </span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortBy)}
                  className="bg-[#12121a] border border-[#D4AF37]/30 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[#F4E8C1] focus:border-[#D4AF37] focus:outline-none transition-colors"
                >
                  <option value="featured">En vedette</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                  <option value="rating">Meilleur avis</option>
                  <option value="name">Nom A → Z</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between mb-6 text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/55">
            <span>
              {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
              {activeCategory !== 'Toutes' && ` · ${activeCategory}`}
            </span>
            <span className="hidden sm:inline">
              Cliquez pour détails · Cœur pour favoris
            </span>
          </div>

          {/* Products grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {filtered.map((product) => {
              const avgRating = getAverageRating(product.id);
              const reviewCount = getReviewCount(product.id);
              const favorited = inWishlist(product.id);
              return (
                <article
                  key={product.id}
                  onClick={() => handleCardClick(product)}
                  className="lift-card group relative bg-[#12121a] border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-colors flex flex-col cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                      style={{ filter: 'brightness(0.85) saturate(1.08)' }}
                    />
                    <div
                      className="absolute inset-0 opacity-50"
                      style={{
                        background:
                          'radial-gradient(ellipse at center, transparent 40%, rgba(10,10,15,0.6) 100%)',
                      }}
                    />
                    {product.tag && (
                      <div className="absolute top-3 left-3 bg-gold-gradient text-[#0a0a0f] text-[8px] sm:text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-1">
                        {product.tag}
                      </div>
                    )}
                    <div className="absolute top-3 right-3 bg-[#0a0a0f]/80 backdrop-blur-sm border border-[#D4AF37]/40 px-2 py-1 font-display text-xs sm:text-sm font-bold text-gold-gradient">
                      {product.price.toLocaleString('fr-BE')} €
                    </div>

                    {/* Wishlist heart button */}
                    <button
                      onClick={(e) => handleWishlist(e, product)}
                      className={`absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center backdrop-blur-sm border transition-all duration-300 ${
                        favorited
                          ? 'bg-[#CE1126] border-[#CE1126] text-white'
                          : 'bg-[#0a0a0f]/70 border-[#D4AF37]/40 text-[#F4E8C1] hover:text-[#CE1126] hover:border-[#CE1126]'
                      }`}
                      aria-label="Ajouter aux favoris"
                    >
                      <Heart size={14} className={favorited ? 'fill-white' : ''} />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-4 flex flex-col flex-1">
                    <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/85 mb-1.5">
                      {product.category}
                    </div>
                    <h3 className="font-display text-sm sm:text-base font-semibold text-[#F4E8C1] group-hover:text-[#D4AF37] transition-colors mb-1.5 leading-tight">
                      {product.name}
                    </h3>

                    {reviewCount > 0 && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star
                              key={s}
                              size={9}
                              className={s <= Math.round(avgRating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#D4AF37]/25'}
                            />
                          ))}
                        </div>
                        <span className="text-[9px] text-[#F4E8C1]/55">
                          {avgRating.toFixed(1)} ({reviewCount})
                        </span>
                      </div>
                    )}

                    <p className="text-[11px] text-[#F4E8C1]/55 leading-relaxed font-light mb-4 flex-1 line-clamp-2">
                      {product.description}
                    </p>

                    <button
                      onClick={(e) => handleAdd(e, product)}
                      className={`w-full inline-flex items-center justify-center gap-2 py-2.5 text-[10px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 ${
                        added === product.id
                          ? 'bg-[#D4AF37] text-[#0a0a0f]'
                          : 'border border-[#D4AF37]/30 text-[#F4E8C1] hover:bg-gold-gradient hover:text-[#0a0a0f] hover:border-[#D4AF37]'
                      }`}
                    >
                      {added === product.id ? (
                        <>
                          <Check size={12} />
                          Ajouté
                        </>
                      ) : (
                        <>
                          <Plus size={12} />
                          Ajouter
                        </>
                      )}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <div className="font-serif italic text-base sm:text-lg text-[#F4E8C1]/70 mb-6 max-w-2xl mx-auto">
              « Une pièce qui vous tente ? Réservez-la dès maintenant. Chaque
              commande est confirmée personnellement par le maître artisan. »
            </div>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#D4AF37]/40 text-[#F4E8C1] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-all text-xs uppercase tracking-[0.2em]"
            >
              <ShoppingBag size={14} />
              Commander sur mesure
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-[#D4AF37]/15 pt-8">
            {[
              { value: 'Livraison 24-48h', sub: 'Belgique · Offerte +500€' },
              { value: 'Paiement sécurisé', sub: 'Carte, virement, WhatsApp' },
              { value: 'Garantie à vie', sub: 'Réparation atelier à Anvers' },
              { value: 'Retrait boutique', sub: 'Rue Nationale 14, Anvers' },
            ].map((t) => (
              <div key={t.value} className="text-center">
                <div className="font-display text-xs sm:text-sm font-semibold text-gold-gradient tracking-wide">
                  {t.value}
                </div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 mt-1">
                  {t.sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </>
  );
}
